import React, { useState, useRef, useContext } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { MyContext } from "../../MyContextApi";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import TablePagination from "@mui/material/TablePagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand, faMinimize } from "@fortawesome/free-solid-svg-icons";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { Tooltip } from "@mui/material";
import Divider from "@mui/material/Divider";

import "./../../styles/TextAnalysisTable.css";

// Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#1AACAC",
    color: theme.palette.common.white,
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "1rem",
    padding: theme.spacing(1.2),
    lineHeight: "1.2rem",
    border: "none", // Reset all borders
    borderRight: `1px solid ${theme.palette.divider}`, // Only add right border
    "&:last-child": {
      borderRight: "none", // Remove right border from last column
    },
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    backgroundColor: theme.palette.common.white,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    padding: theme.spacing(1.2),
    border: `0.5px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderRight: "none", // Remove right border from last column
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: "rgba(249, 84, 84, 0.04)",
    transition: "background-color 0.2s ease",
  },
}));

function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onPageChange, paginatedData } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  const disableButtons = paginatedData.length === 0;

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={disableButtons || page === 0}
        aria-label="first page"
      >
        <FirstPageIcon />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={disableButtons || page === 0}
        aria-label="previous page"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={disableButtons || page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={disableButtons || page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPageIcon />
      </IconButton>
    </Box>
  );
}

function TextAnalysisTable({ analysisData = [], tableRef }) {
  const { navigationCount, setNavigationCount } = useContext(MyContext);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const tableContainerRef = useRef();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen) {
      tableContainerRef.current.requestFullscreen();
      setIsFullscreen(isFullscreen);
    } else if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  };

  const handleChangePage = (event, newPage) => {
    if (newPage > page) {
      setNavigationCount((prev) => prev + 1);
    } else {
      setNavigationCount((prev) => prev - 1);
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = Array.isArray(analysisData)
    ? analysisData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  // Helper function to check if any data item has a specific field
  const hasAnyField = (fieldName) => {
    return (
      Array.isArray(analysisData) &&
      analysisData.some(
        (item) =>
          item[fieldName] !== undefined &&
          item[fieldName] !== null &&
          item[fieldName] !== "" &&
          (Array.isArray(item[fieldName]) ? item[fieldName].length > 0 : true)
      )
    );
  };

  const showPlatform = hasAnyField("platform");
  const showUserName = hasAnyField("postOwner") || hasAnyField("useHandle");
  const showDateTime = hasAnyField("datetime");
  const showKeyword = hasAnyField("keyword");
  const showLanguage = hasAnyField("languages");
  const showSection = hasAnyField("sections");
  const showIntensity = hasAnyField("intensity");
  const showContent =
    hasAnyField("content") ||
    hasAnyField("title") ||
    hasAnyField("link") ||
    hasAnyField("source_link");
  const lowercaseRomanNumerals = (text) => {
    return text
      .replace(/\((?=[IVXLCDM]+\))/g, "(")
      .replace(/\b[IVXLCDM]+\b/g, (match) => match.toLowerCase());
  };

  return (
    <Box
      sx={{
        width: isFullscreen ? "100vw" : "auto",
        height: isFullscreen ? "100vh" : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "end",
        overflow: "hidden",
        position: "relative",
        zIndex: 0,
        borderRadius: "15px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.25)",
      }}
    >
      <TableContainer
        component={Paper}
        className="custom-table-container"
        ref={tableContainerRef}
      >
        <Table
          stickyHeader
          sx={{ minWidth: 1200, tableLayout: "fixed" }}
          aria-label="customized table"
          ref={tableRef}
        >
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: 75 }}>S.No</StyledTableCell>
              {showPlatform && (
                <StyledTableCell sx={{ width: 115 }}>Platform</StyledTableCell>
              )}
              {showUserName && (
                <StyledTableCell sx={{ width: 150 }}>User Name</StyledTableCell>
              )}
              {showDateTime && (
                <StyledTableCell sx={{ width: 100 }}>DateTime</StyledTableCell>
              )}
              {showKeyword && (
                <StyledTableCell sx={{ width: 100 }}>Keyword</StyledTableCell>
              )}
              {showLanguage && (
                <StyledTableCell sx={{ width: 100 }}>Language</StyledTableCell>
              )}
              {showSection && (
                <StyledTableCell sx={{ width: 100 }}>Section</StyledTableCell>
              )}
              {showIntensity && (
                <StyledTableCell sx={{ width: 80 }}>Intensity</StyledTableCell>
              )}
              {showContent && (
                <StyledTableCell sx={{ width: 400 }}>Content</StyledTableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 &&
              paginatedData.map((data, index) => {
                const formattedDateTime = data.datetime
                  ? data.datetime
                      .replace("T", " ")
                      .split(".")[0]
                      .replace(" ", " / ")
                  : "N/A";

                return (
                  <StyledTableRow key={index}>
                    <StyledTableCell>
                      {page * rowsPerPage + index + 1}
                    </StyledTableCell>

                    {showPlatform && (
                      <StyledTableCell>
                        {data.platform
                          ? data.platform.charAt(0).toUpperCase() +
                            data.platform.slice(1).toLowerCase()
                          : "N/A"}
                      </StyledTableCell>
                    )}

                    {showUserName && (
                      <StyledTableCell>
                        {data.useHandle ? (
                          <a
                            href={data.useHandle}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {data.postOwner || "N/A"}
                          </a>
                        ) : (
                          data.postOwner || "N/A"
                        )}
                      </StyledTableCell>
                    )}

                    {showDateTime && (
                      <StyledTableCell>
                        {data.datetime ? (
                          <>
                            {new Intl.DateTimeFormat("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "2-digit",
                              timeZone: "UTC",
                            }).format(new Date(data.datetime))}
                            , 
                            {new Intl.DateTimeFormat("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                              timeZone: "UTC",
                            }).format(new Date(data.datetime))}
                          </>
                        ) : (
                          "N/A"
                        )}
                      </StyledTableCell>
                    )}

                    {showKeyword && (
                      <StyledTableCell>{data.keyword || "N/A"}</StyledTableCell>
                    )}

                    {showLanguage && (
                      <StyledTableCell>
                        {data.languages || "N/A"}
                      </StyledTableCell>
                    )}
                    {showSection && (
                      <StyledTableCell>
                        {Array.isArray(data.sections) &&
                        data.sections.length > 0
                          ? data.sections.map((section, sectionIndex) => (
                              <span key={sectionIndex}>
                                {sectionIndex > 0 && ", "}
                                {lowercaseRomanNumerals(
                                  section.includes("Section")
                                    ? section.split("Section ")[1]
                                    : section
                                )}
                              </span>
                            ))
                          : "N/A"}
                      </StyledTableCell>
                    )}

                    {showIntensity && (
                      <StyledTableCell>
                        {data.intensity
                          ? data.intensity.charAt(0).toUpperCase() +
                            data.intensity.slice(1).toLowerCase()
                          : "N/A"}
                      </StyledTableCell>
                    )}

                    {showContent && (
                      <StyledTableCell>
                        {data.platform === "YOUTUBE" ? (
                          data.source_link ? (
                            <a
                              href={data.source_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "black", textDecoration: "none" }}
                            >
                              <div>
                                <span
                                  style={{
                                    display: "inline-block",
                                    backgroundColor: "#e3f2fd",
                                    color: "#0288d1",
                                    padding: "2px 8px",
                                    borderRadius: "12px",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    marginBottom: "10px",
                                  }}
                                >
                                  Original
                                </span>
                                {data.title && (
                                  <p style={{ margin: 0 }}>{data.title}</p>
                                )}
                                {data.titleTranslation && (
                                  <div style={{ marginTop: "10px" }}>
                                    <span
                                      style={{
                                        display: "inline-block",
                                        backgroundColor: "#e0f7fa",
                                        color: "#00796b",
                                        padding: "2px 8px",
                                        borderRadius: "12px",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                        marginBottom: "5px",
                                      }}
                                    >
                                      Translation
                                    </span>
                                    <p
                                      style={{
                                        margin: "5px 0 0",
                                        fontStyle: "italic",
                                        color: "#888",
                                      }}
                                    >
                                      {data.titleTranslation}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </a>
                          ) : (
                            <div>
                              <span
                                style={{
                                  display: "inline-block",
                                  backgroundColor: "#e3f2fd",
                                  color: "#0288d1",
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  marginBottom: "10px",
                                }}
                              >
                                Original
                              </span>
                              {data.title && (
                                <p style={{ margin: 0 }}>{data.title}</p>
                              )}
                              {data.titleTranslation && (
                                <div style={{ marginTop: "10px" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      backgroundColor: "#e0f7fa",
                                      color: "#00796b",
                                      padding: "2px 8px",
                                      borderRadius: "12px",
                                      fontSize: "12px",
                                      fontWeight: "bold",
                                      marginBottom: "5px",
                                    }}
                                  >
                                    Translation
                                  </span>
                                  <p
                                    style={{
                                      margin: "5px 0 0",
                                      fontStyle: "italic",
                                      color: "#888",
                                    }}
                                  >
                                    {data.titleTranslation}
                                  </p>
                                </div>
                              )}
                            </div>
                          )
                        ) : data.link ? (
                          <a
                            href={data.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "black", textDecoration: "none" }}
                          >
                            <div>
                              <span
                                style={{
                                  display: "inline-block",
                                  backgroundColor: "#e3f2fd",
                                  color: "#0288d1",
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  marginBottom: "10px",
                                }}
                              >
                                Original
                              </span>
                              <p style={{ margin: 0 }}>
                                {data.content || "N/A"}
                              </p>
                              {data.translation && (
                                <div style={{ marginTop: "10px" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      backgroundColor: "#e0f7fa",
                                      color: "#00796b",
                                      padding: "2px 8px",
                                      borderRadius: "12px",
                                      fontSize: "12px",
                                      fontWeight: "bold",
                                      marginBottom: "5px",
                                    }}
                                  >
                                    Translation
                                  </span>
                                  <p
                                    style={{
                                      margin: "5px 0 0",
                                      fontStyle: "italic",
                                      color: "#888",
                                    }}
                                  >
                                    {data.translation}
                                  </p>
                                </div>
                              )}
                            </div>
                          </a>
                        ) : (
                          <div>
                            <span
                              style={{
                                display: "inline-block",
                                backgroundColor: "#e3f2fd",
                                color: "#0288d1",
                                padding: "2px 8px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: "bold",
                                marginBottom: "10px",
                              }}
                            >
                              Original
                            </span>
                            <p style={{ margin: 0 }}>{data.content || "N/A"}</p>
                            {data.translation && (
                              <div style={{ marginTop: "10px" }}>
                                <span
                                  style={{
                                    display: "inline-block",
                                    backgroundColor: "#e0f7fa",
                                    color: "#00796b",
                                    padding: "2px 8px",
                                    borderRadius: "12px",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    marginBottom: "5px",
                                  }}
                                >
                                  Translation
                                </span>
                                <p
                                  style={{
                                    margin: "5px 0 0",
                                    fontStyle: "italic",
                                    color: "#888",
                                  }}
                                >
                                  {data.translation}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </StyledTableCell>
                    )}
                  </StyledTableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          outline: "none",
          backgroundColor: "white",
          borderBottomRightRadius: "15px",
          borderBottomLeftRadius: "15px",
        }}
      >
        <Tooltip title="Expand">
          <Button onClick={toggleFullscreen}>
            {isFullscreen ? (
              <FontAwesomeIcon icon={faMinimize} fontSize={"15px"} />
            ) : (
              <FontAwesomeIcon icon={faExpand} fontSize={"20px"} />
            )}
          </Button>
        </Tooltip>
        <TablePagination
          component="div"
          count={Array.isArray(analysisData) ? analysisData.length : 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={(props) => (
            <TablePaginationActions {...props} paginatedData={paginatedData} />
          )}
          sx={{
            minWidth: "auto",
            height: "50px",
            "& .MuiTablePagination-toolbar": {
              minHeight: "50px",
              padding: "0 8px",
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "15px",
                margin: 0,
              },
            "& .MuiTablePagination-actions": {
              padding: 0,
            },
            "& .MuiSelect-select": {
              fontSize: "15px",
              padding: "4px 8px",
            },
          }}
        />
      </Box>
    </Box>
  );
}

export default TextAnalysisTable;
